import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/**
 * Renders the current End-User Agreement.
 * Reachable at `/eula` (public) and linked from the signup checkbox.
 */
export default function Eula() {
    const [content, setContent] = useState({ version: "", markdown: "" });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/eula")
            .then((r) => setContent(r.data))
            .catch(() => toast.error("Couldn't load agreement"))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="min-h-screen bg-slate-50" data-testid="eula-page">
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <BrandLogo height={34} />
                    </Link>
                    <Link to="/register">
                        <Button size="sm" className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-9">Back to signup</Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 py-10">
                {loading ? (
                    <p className="text-slate-500">Loading agreement…</p>
                ) : (
                    <div className="bg-white rounded-md border border-slate-200 shadow-sm p-8 md:p-12" data-testid="eula-content">
                        <MarkdownView markdown={content.markdown} />
                        <p className="text-xs text-slate-400 mt-8 font-mono">Version: {content.version}</p>
                    </div>
                )}
            </main>
        </div>
    );
}

// Minimal markdown → HTML renderer scoped to the EULA text (headings, lists,
// bold, italics, hr, paragraphs). Avoids pulling a heavyweight md library for
// a static page we control.
function MarkdownView({ markdown }) {
    const html = mdToHtml(markdown || "");
    return (
        <div
            className="prose-eula"
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

function escapeHtml(s) {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function inline(t) {
    return escapeHtml(t)
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/`(.+?)`/g, "<code>$1</code>");
}

function mdToHtml(md) {
    const lines = md.replace(/\r\n/g, "\n").split("\n");
    const out = [];
    let inList = false;
    let paraBuf = [];
    let listItemBuf = null;

    const flushPara = () => {
        if (paraBuf.length) {
            out.push(`<p style="margin:0 0 14px;color:#334155;line-height:1.65;">${inline(paraBuf.join(" "))}</p>`);
            paraBuf = [];
        }
    };
    const openList = () => {
        if (!inList) {
            out.push(`<ul style="margin:0 0 14px;padding-left:22px;color:#334155;line-height:1.65;">`);
            inList = true;
        }
    };
    const flushListItem = () => {
        if (listItemBuf) {
            out.push(`<li style="margin-bottom:6px;">${inline(listItemBuf)}</li>`);
            listItemBuf = null;
        }
    };
    const closeList = () => {
        flushListItem();
        if (inList) { out.push("</ul>"); inList = false; }
    };

    for (const raw of lines) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            flushPara();
            closeList();
            continue;
        }
        if (line.startsWith("# ")) {
            flushPara(); closeList();
            out.push(`<h1 style="font-family:Georgia,serif;color:#0F1E3D;font-size:32px;margin:0 0 18px;">${inline(line.slice(2))}</h1>`);
            continue;
        }
        if (line.startsWith("## ")) {
            flushPara(); closeList();
            out.push(`<h2 style="font-family:Georgia,serif;color:#0F1E3D;font-size:22px;margin:32px 0 10px;">${inline(line.slice(3))}</h2>`);
            continue;
        }
        if (line.startsWith("### ")) {
            flushPara(); closeList();
            out.push(`<h3 style="color:#0F1E3D;font-size:16px;margin:18px 0 6px;font-weight:600;">${inline(line.slice(4))}</h3>`);
            continue;
        }
        if (line === "---") {
            flushPara(); closeList();
            out.push(`<hr style="border:none;border-top:1px solid #E2E8F0;margin:24px 0;"/>`);
            continue;
        }
        if (/^[-*] /.test(line)) {
            flushPara();
            flushListItem();
            openList();
            listItemBuf = line.replace(/^[-*] /, "");
            continue;
        }
        // Indented continuation line INSIDE a list → append to current item
        if (inList && /^\s+/.test(raw) && listItemBuf !== null) {
            listItemBuf += " " + line.trim();
            continue;
        }
        // Regular paragraph line — accumulate.
        if (inList) closeList();
        paraBuf.push(line);
    }
    flushPara();
    closeList();
    return out.join("");
}
